import { useCallback, useEffect, useRef } from 'react';

/** How often the countdown is read, not how long it is: fine enough to look immediate. */
const IDLE_CHECK_MS = 250;

type IdleResetOptions = {
  readonly isArmed: boolean;
  readonly resetAfterMs: number;
  readonly onReset: () => void;
};

/**
 * A search panel left mid-query: `onReset` is called once `resetAfterMs` has
 * passed with no activity, and only while `isArmed` — there is no point
 * counting down towards clearing something that is already clear. It fires at
 * most once per arming, so a panel nobody clears does not reset on a loop.
 *
 * Only what the returned `noteActivity` is wired to counts as activity, which
 * is the point: this watches one panel, not the window like `useIdleDismiss`.
 * The last-activity stamp is a ref rather than state so scrolling the results
 * does not re-render the list under the finger doing the scrolling.
 */
export const useIdleReset = ({
  isArmed,
  resetAfterMs,
  onReset,
}: IdleResetOptions): (() => void) => {
  const draftLastActivityAt = useRef(0);
  const draftOnReset = useRef(onReset);

  useEffect(() => {
    draftOnReset.current = onReset;
  }, [onReset]);

  const noteActivity = useCallback((): void => {
    draftLastActivityAt.current = Date.now();
  }, []);

  useEffect(() => {
    if (!isArmed) {
      return;
    }

    draftLastActivityAt.current = Date.now();

    const timer = setInterval(() => {
      if (Date.now() - draftLastActivityAt.current < resetAfterMs) {
        return;
      }

      clearInterval(timer);
      draftOnReset.current();
    }, IDLE_CHECK_MS);

    return (): void => {
      clearInterval(timer);
    };
  }, [isArmed, resetAfterMs]);

  return noteActivity;
};
